/** @format */

import {
	Box,
	Button,
	Divider,
	Flex,
	FormControl,
	Input,
	InputGroup,
	InputRightElement,
	Link,
	SimpleGrid,
	Skeleton,
	SkeletonCircle,
	Spinner,
	Text,
} from '@chakra-ui/react';
import axios from 'axios';
import {Dispatch, SetStateAction, useEffect, useState} from 'react';
import {useDebounce} from 'use-debounce';
import contentData from '../../../../data/contentData';
import IconTickLime from '../../../../icons/IconTickLime';
import {PromoCode} from '../../../../pages/api/stripe/checkPromoCode';
import {
	CheckoutPackage,
	StripeProductsObject,
} from '../../../../pages/join-the-assc/join';
import {colors} from '../../../../styles/js/colors';
import {textStyles} from '../../../../styles/js/textStyles';
import StripeAcceptPayment from '../../../StripeAcceptPayment';
import {scrollToTop} from '../../../../utils';
import {User} from '../../../../pages/api/user';
import Stripe from 'stripe';

export type MemberPackage = {
	title: string;
	packageSummary: string;
	packageTooltip: string;
	price: number;
	marketingPackage?: {
		summary: string;
		bullets: string[];
		price: number;
	};
};

const PaymentLeft = ({
	user,
	selectedPackage,
	memberPackage,
	setSelectedPackage,
	products,
	activePromo,
	setActivePromo,
	prevStep,
}: {
	user: User & {
		stripe?: {
			stripeUser?: Stripe.Customer;
			stripeSubscription?: Stripe.Subscription;
		};
	};
	selectedPackage: CheckoutPackage;
	memberPackage: MemberPackage;
	setSelectedPackage: Dispatch<SetStateAction<CheckoutPackage>>;
	products: StripeProductsObject;
	activePromo?: PromoCode;
	setActivePromo: Dispatch<SetStateAction<PromoCode | undefined>>;
	prevStep: () => void;
}) => {
	const [promoInput, setPromoInput] = useState('');
	const [debouncedPromoInput] = useDebounce(promoInput, 1000);
	const handlePromoInputChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setPromoInput(event.target.value);
	};

	const [initialPackage, setInitialPackage]
		= useState<CheckoutPackage>(selectedPackage);

	const [isPromoLoading, setIsPromoLoading] = useState(false);
	const [paymentIntent, setPaymentIntent] = useState<{
		amount: number;
		currency: string;
		status: string;
	}>(
		null,
	);
	const [paymentLoading, setPaymentLoading] = useState(false);

	useEffect(() => {
		const fetchPaymentIntent = async () => {
			setPaymentLoading(true);
			// delay some seconds to allow stripe to update (yes this is a hack, but it works for now)
			// eslint-disable-next-line no-promise-executor-return
			await new Promise(resolve => setTimeout(resolve, 2500));
			await axios.post('/api/stripe/getLatestPaymentIntent', {
				customerId: user.stripe?.stripeUser?.id,
			}).then(res => {
				setPaymentLoading(false);
				if (!paymentIntent || paymentIntent.amount !== res.data.amount) {
					setPaymentIntent(res.data);
				}
			});
		};

		if (user.stripe) {
			void fetchPaymentIntent();
		}
	}, [user, activePromo]);

	useEffect(() => {
		if (activePromo?.isValid) {
			setSelectedPackage(prevState => {
				const discount = activePromo?.discount;
				const discountAmount = discount?.amountOff || 0;
				const discountPercent = discount?.percentOff || 0;
				const discountedSubTotal = prevState.subTotal - discountAmount;
				const discountedSubTotalWithPercent
					= (discountedSubTotal * (100 - discountPercent)) / 100;

				return {
					...prevState,
					// vat: ((discountedSubTotalWithPercent * 0.2) / 100).toFixed(2),
					subTotal: discountedSubTotal,
					// // prettier-ignore
					// total: (
					// 	// eslint-disable-next-line no-mixed-operators
					// 	(discountedSubTotalWithPercent +
					// 		// eslint-disable-next-line no-mixed-operators
					// 		discountedSubTotalWithPercent * 0.2) /
					// 	100
					// ).toFixed(2),
				};
			});
		} else {
			setSelectedPackage(initialPackage);
		}
	}, [activePromo]);

	const debouncedSearch = async (promoCode: string) => {
		try {
			setIsPromoLoading(true);
			const response = await axios.post<PromoCode>(
				'/api/stripe/checkPromoCode',
				{promoCode},
			);
			if (response.data?.isValid) {
				setIsPromoLoading(false);
				setActivePromo(response.data);
			} else {
				setIsPromoLoading(false);
				setActivePromo(undefined);
			}
		} catch (error: unknown) {
			console.error(error);
			setActivePromo(undefined);
			setIsPromoLoading(false);
		}
	};

	useEffect(() => {
		if (!debouncedPromoInput) {
			setActivePromo(undefined);
			return;
		}

		void debouncedSearch(debouncedPromoInput);
	}, [debouncedPromoInput]);

	return (
		<SimpleGrid columns={1} spacing={1.5} bgColor={colors.white} maxW='22.5rem'>
			<Text sx={textStyles.display5LeftDark}>Payment summary:</Text>
			<Text sx={textStyles.paragraph2LeftItalicDark} mb={8}>
				Please check your membership summary:
			</Text>
			<SimpleGrid
				columns={1}
				borderRadius={8}
				backgroundColor={colors.slate[100]}
				boxShadow='lg'
				maxW={{base: '100%', md: '22.5rem'}}
				flexGrow='1'
				flexDirection='column'
				justifyContent='space-between'
				px={6}
				py={4}
				spacing={4}>
				<Flex>
					<Text sx={textStyles.paragraph2LeftDark} flexGrow='1'>
						{products?.membership?.product?.name
						&& products?.joiningFee?.product?.name
							? products.membership.product.name
							: memberPackage.title}
					</Text>
					<Text sx={textStyles.paragraph2LeftDark}>
						£
						{products?.membership?.product?.price?.unit_amount
						&& products?.joiningFee?.product?.price?.unit_amount
							? (
								(products.membership.product.price.unit_amount +
										products.joiningFee.product.price.unit_amount) /
									100
							  ).toFixed(2)
							: memberPackage.price}
					</Text>
				</Flex>
				{selectedPackage.package.marketingPackageSelected && (
					<>
						<Divider borderWidth={1} borderColor={colors.slate[500]} />
						<Flex>
							<Box flexGrow='1'>
								<Text sx={textStyles.paragraph2LeftDark} noOfLines={1}>
									Marketing Package
								</Text>
								<Text sx={textStyles.paragraph2LeftGrey}>
									This includes marketing for one property on one website page
								</Text>
							</Box>
							<Text sx={textStyles.paragraph2LeftDark}>
								£
								{products?.marketing?.product?.price?.unit_amount
									? (
										products.marketing.product.price.unit_amount / 100
									  ).toFixed(2)
									: memberPackage.marketingPackage.price / 100}
							</Text>
						</Flex>
					</>
				)}
				{selectedPackage.package.additionalSubscriptions.map(
					subscription =>
						subscription.quantity > 0 && (
							<Flex key={subscription.name}>
								<Box flexGrow='1'>
									<Text sx={textStyles.paragraph2LeftDark} noOfLines={1}>
										{subscription.name}
									</Text>
								</Box>
								{subscription.prodRef === 'additionalWebpage'
									&& products?.additionalWebpage?.product?.price?.unit_amount && (
									<Text sx={textStyles.paragraph2LeftDark}>
											£
										{(
											products.additionalWebpage.product.price.unit_amount /
												100
										).toFixed(2)}{' '}
											x {subscription.quantity}
									</Text>
								)}
								{subscription.prodRef === 'additionalProperty'
									&& products?.additionalProperty?.product?.price?.unit_amount && (
									<Text sx={textStyles.paragraph2LeftDark}>
											£
										{(
											products.additionalProperty.product.price.unit_amount /
												100
										).toFixed(2)}{' '}
											x {subscription.quantity}
									</Text>
								)}
							</Flex>
						),
				)}
				<Divider borderWidth={1} borderColor={colors.slate[500]} />
				<Flex>
					<Text sx={textStyles.paragraph2LeftDark} noOfLines={1} flexGrow='1'>
						VAT
					</Text>
					<Text sx={textStyles.paragraph2LeftDark}>
						£{selectedPackage?.vat}
					</Text>
				</Flex>
				<Flex>
					<Text sx={textStyles.paragraph2LeftDark} noOfLines={1} flexGrow='1'>
						Sub-total
					</Text>
					<Text sx={textStyles.paragraph2LeftDark}>
						£{selectedPackage.subTotal / 100}
					</Text>
				</Flex>
				{activePromo?.discount && (
					<Flex>
						<Text
							sx={textStyles.paragraph2LeftLightGreen}
							noOfLines={1}
							flexGrow='1'>
							Promo:
						</Text>
						<Text sx={textStyles.paragraph2LeftLightGreen}>
							{activePromo.discount?.currency
								? `-£${activePromo.discount.amountOff / 100}`
								// : `-${activePromo.discount.percentOff}%`
								: 'Percentage Discount Applied'
							}
						</Text>
					</Flex>
				)}
				<Divider borderWidth={1} borderColor={colors.slate[500]} />
				<Flex>
					<Text sx={textStyles.paragraph1LeftDark} noOfLines={1} flexGrow='1'>
						Total
					</Text>
					<Skeleton isLoaded={!paymentLoading}>
						<Text sx={textStyles.paragraph1LeftDark} noOfLines={1}>
							£{
								(paymentIntent?.amount && (paymentIntent.amount / 100).toFixed(2))
								|| selectedPackage.total
							}
						</Text>
					</Skeleton>
				</Flex>
			</SimpleGrid>
			<SimpleGrid
				columns={1}
				flexGrow='1'
				flexDirection='column'
				justifyContent='space-between'
				py={4}
				spacing={1.5}>
				<Text sx={textStyles.paragraph2LeftDark}>Promotional code:</Text>

				<FormControl>
					<InputGroup>
						<Input
							value={promoInput}
							onChange={handlePromoInputChange}
							placeholder='Enter code'
						/>
						{isPromoLoading ? (
							<InputRightElement>
								<Spinner size='sm' color='lime.500' />
							</InputRightElement>
						) : (
							activePromo?.isValid && (
								<InputRightElement pointerEvents='none'>
									<IconTickLime />
								</InputRightElement>
							)
						)}
					</InputGroup>
				</FormControl>
				{/* {activePromo?.discount?.percentOff === 100 ? (
					<Link
						variant='native'
						p='0'
						alignItems={'left'}
						target='_blank'
						rel='noopener noreferrer'
						href={`mailto:${process.env.NEXT_PUBLIC_MAILCHIMP_TRANSACTIONAL_MAIL_TO_MEMBERSHIP}?subject=Using coupon with this discount`}
						sx={textStyles.paragraph2LeftDark}>
						Please contact assc.co.uk regarding the use of this coupon:
					</Link>
				) : null} */}
			</SimpleGrid>
			<Box mt={32}>
				<Text
					onClick={prevStep}
					variant='primary'
					textStyle='buttonCentreAquaGreen'
					textAlign='left'
					mb={8}
					_hover={{
						textDecoration: 'underline',
						cursor: 'pointer',
						color: colors.aquaGreen[300],
					}}>
					{'< Back'}
				</Text>
				<Text sx={textStyles.paragraph2LeftDarkSlate}>Powered by Stripe</Text>
			</Box>
		</SimpleGrid>
	);
};

type PaymentRightProps = {
	nextStep: () => void;
	sendEmailTemplateAfterChequePaymentConfirm?: () => void;
	selectedPackage: CheckoutPackage;
	stripeReturnUrlSubDirectory?: string;
};

const PaymentRight = ({
	nextStep,
	selectedPackage,
	sendEmailTemplateAfterChequePaymentConfirm,
	...rest
}: PaymentRightProps) => {
	const [paymentViewOption, setPaymentViewOption] = useState('StripeComponent');
	const [lastRadioChoice, setLastRadioChoice] = useState('1');
	const [paymentRadio, setPaymentRadio] = useState(lastRadioChoice);

	const PaymentDetails = ({selectedPackage}) => {
		const handlePaymentClick = () => {
			setPaymentViewOption('Cheque');
			scrollToTop();
		};

		return (
			<SimpleGrid
				columns={1}
				spacing={6}
				p={6}
				pt={{base: 12, md: 140}}
				pl={{base: 6, md: 28}}
				pb={{base: 12, md: 32}}
				bgColor={colors.tweed[300]}
				justifyItems={{base: 'center', md: 'start'}}>
				<Box maxW='22.5rem'>
					<Text sx={textStyles.display5LeftDark} mb={1.5}>
						Payment details:
					</Text>
					<Text sx={textStyles.paragraph2LeftItalicDark} mb={8}>
						Complete your membership by providing your payment details:
					</Text>
					<StripeAcceptPayment
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						selectedPackage={selectedPackage}
						{...rest}
					/>
					{/* <SimpleGrid
						columns={1}
						maxW={{base: '100%'}}
						flexGrow='1'
						flexDirection='column'
						spacing={4}>
						<SimpleGrid
							columns={1}
							borderWidth={2}
							borderRadius={8}
							borderColor={colors.tweed[500]}
							borderStyle={'dashed'}
							p={6}>
							<Box>
								<Text sx={textStyles.paragraph2LeftItalicDark}>
									If you are unable to pay by card, you can also
									<Link
										onClick={handlePaymentClick}
										variant='native'
										pl={1}
										textStyle={{
											base: 'paragraph1LeftItalicDark',
										}}>
										pay by cheque
									</Link>
									.
								</Text>
							</Box>
						</SimpleGrid>
					</SimpleGrid> */}
				</Box>
			</SimpleGrid>
		);
	};

	const PaymentDetailsCheque = () => {
		const handleChequeConfirmPaymentMethodClick = value => {
			setLastRadioChoice(value);
			// setPaymentViewOption('StripeComponent');
			sendEmailTemplateAfterChequePaymentConfirm();
		};

		const handleChequeCancelClick = () => {
			setPaymentRadio(lastRadioChoice);
			setPaymentViewOption('StripeComponent');
			scrollToTop();
		};

		return (
			<SimpleGrid
				columns={1}
				spacing={6}
				bgColor={colors.tweed[300]}
				p={6}
				pt={{base: 12, md: 140}}
				pl={{base: 6, md: 28}}
				pb={{base: 6, md: 32}}>
				<SimpleGrid
					columns={1}
					maxW={{base: '100%', md: 80}}
					flexGrow='1'
					flexDirection='column'
					justifyContent='stretch'
					spacing={4}>
					<Text sx={textStyles.display5LeftDark}>Cheque payments</Text>
					<Text sx={textStyles.paragraph2LeftItalicDark}>
						{contentData.paymentMethods[paymentRadio].instructions}
					</Text>
					<Box
						borderWidth={2}
						borderRadius={8}
						borderColor={colors.tweed[500]}
						borderStyle={'dashed'}
						p={6}
						maxH='150px'>
						{/* eslint-disable-next-line @typescript-eslint/no-unsafe-call */}
						{contentData.paymentMethods[paymentRadio].details?.map(row => (
							// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
							<Text key={row} sx={textStyles.paragraph2LeftItalicDark}>
								{row}
							</Text>
						))}
					</Box>
					<Text sx={textStyles.paragraph2LeftItalicDark}>
						Membership will be processed as soon as possible.
					</Text>
					<SimpleGrid
						columns={1}
						flexDirection='column'
						justifyContent='stretch'>
						<Button
							onClick={() => {
								handleChequeConfirmPaymentMethodClick(paymentRadio);
							}}>
							Confirm payment method
						</Button>
						<Button variant='secondary' onClick={handleChequeCancelClick}>
							Cancel
						</Button>
					</SimpleGrid>
				</SimpleGrid>
			</SimpleGrid>
		);
	};

	// if (paymentViewOption === 'Cheque') {
	// 	return <PaymentDetailsCheque />;
	// }

	return <PaymentDetails selectedPackage={selectedPackage} />;
};

export {PaymentLeft, PaymentRight};
